import { configureStore } from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";
import { combineReducers } from "redux";
import { persistReducer, persistStore } from "redux-persist";
import storage from "redux-persist/lib/storage";

import { baseApi } from "~/services/api";
import cycleCountCacheReducer from "~/store/cycleCountCacheSlice";
import offlineQueueReducer from "~/store/offlineQueueSlice";
import settingsReducer from "~/store/settingsSlice";
import siteReducer from "~/store/siteSlice";

/**
 * The cycle-count offline read cache gets its OWN nested `persistReducer` (Atlas: Nested Persists),
 * separate from the outer `settings`/`site` persistence below. That gives it its own storage key
 * (`persist:cycleCountCache`) so `SFAuth.redirectToLogout()` can remove just this entry on logout —
 * PKCE logout deliberately skips the Redux `LOGOUT` root-reset (see the note below) and must not wipe
 * unrelated persisted prefs to compensate. Site-change clearing is handled inside the slice itself
 * (`cycleCountCacheSlice`'s `updateSite` extraReducer) — no root-reducer surgery needed.
 */
const persistedCycleCountCacheReducer = persistReducer({ key: "cycleCountCache", storage }, cycleCountCacheReducer);

const reducer = combineReducers({
	[baseApi.reducerPath]: baseApi.reducer,
	cycleCountCache: persistedCycleCountCacheReducer,
	offlineQueue: offlineQueueReducer,
	settings: settingsReducer,
	site: siteReducer
});

type PersistedState = ReturnType<typeof reducer>;

const persistConfig = {
	key: "redux",
	storage,
	// Explicit whitelist — never a blanket persist. The RTK Query cache (baseApi) and any
	// API-fetched/transient slices stay out; only durable UI/session state is persisted.
	// `site` persists the field user's selected starting node (RequireSite) across reloads.
	// `offlineQueue` is deliberately NOT persisted — the service worker's IndexedDB Background-Sync
	// queue is the source of truth, and the mount-time REQUEST_QUEUE_STATE handshake rehydrates it.
	// `cycleCountCache` is NOT in this outer whitelist — it persists itself via its own nested
	// `persistReducer` above, so it gets its own storage key instead of sharing this one.
	whitelist: ["settings", "site"]
};

/**
 * Resets every slice back to its initial state. NOTE: the PKCE logout flow (SFAuth.redirectToLogout)
 * intentionally does NOT dispatch this — an RTK Query refetch races the storage clear and can
 * silently re-auth via checkSession(). Logout clears tokens + session-bound storage and navigates
 * to the FusionAuth logout endpoint instead.
 */
export const LOGOUT = "LOGOUT";

const rootReducer: typeof reducer = (state, action) => {
	if (action.type === LOGOUT) {
		return reducer(undefined, action);
	}
	return reducer(state, action);
};

const persistedReducer = persistReducer<PersistedState>(persistConfig, rootReducer) as unknown as typeof rootReducer;

export const store = configureStore({
	devTools: process.env.NODE_ENV === "development",
	reducer: persistedReducer,
	middleware: (getDefaultMiddleware) =>
		getDefaultMiddleware({
			// redux-persist dispatches non-serializable actions (FLUSH/REHYDRATE/...); RTK Query
			// caches large response payloads. Both checks are disabled for the same reasons as
			// the worksuite-fleet baseline.
			immutableCheck: false,
			serializableCheck: false
		}).concat(baseApi.middleware)
});

setupListeners(store.dispatch);

export const persistor = persistStore(store);

// Inferred `RootState` and `AppDispatch` from the store itself.
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
